// The Diagnostic Scanner — the Home-page runtime monitor. The transcript stands at the centre of a lean
// 3D conveyor; eleven laser heads dock in turn and sweep the page top-to-bottom (with occasional focus
// dwells, re-scanning a row band), lighting each skill on the side panel. It is paced for the human eye
// and DECOUPLED from real scoring speed: heads play at a fixed viewable rate, and the final head keeps
// sweeping until scoring reports `done`. Then a completion message holds 3s and a cinematic fade hands off
// to Quick Snapshot.
//
// There are NO per-skill events on the wire — like the runtime progress bar it rides the scoring PHASE
// (setPhase → scanLabPhase). CRITICAL: the canvas render loop uses requestAnimationFrame (cosmetic only,
// paused on a hidden tab), but ALL sequencing and the hand-off run on setTimeout so a backgrounded tab
// still completes and navigates. (This module fills the historical #distill-lab host slot.)

const SKILLS = ['Setting Expectations', 'Call Prep', 'Personalization', 'Problem Development', 'The Moneyball Gap', 'A-Ha Moment', 'Objection Handling', 'Competitive Play', 'Domain Expertise', 'Active Listening', 'Next Steps'];
const N = SKILLS.length;
const STEP = (2 * Math.PI) / N;

// per-head base scan durations (ms). short -> one slow readable sweep; longer -> repeated sweeps with
// focus dwells. The FINAL head extends past its base, ping-ponging until scoring says `done`.
const DUR = [1000, 1000, 2200, 1000, 2600, 1500, 1000, 2100, 1000, 1000, 1900];
const HALF = 820;         // ms for one full-length sweep
const ROTATE_MS = 520;    // dock the next head
const DOCK_MS = 180;      // settle before the beam fires
const GAP_MS = 150;       // between heads
const HOLD_MS = 3000;     // completion message hold before the hand-off fade (main owns the fade)
const LAST_CAP = 45000;   // safety: never wait forever on the final head

// camera / geometry
const SW = 660, SH = 560, CXV = SW / 2, CYV = SH * 0.585;
const PANG = (20 * Math.PI) / 180, SINP = Math.sin(PANG), COSP = Math.cos(PANG);
const CH = 190, CD = 520, FOC = 520;
const R = 232, DW = 174, DH = 258; // apparatus enlarged ~15% to fill more of the canvas

type Proj = { X: number; Y: number; s: number; depth: number };
function proj(x: number, y: number, z: number): Proj {
  const ry0 = y - CH, rz0 = z - CD;
  const ry = ry0 * COSP - rz0 * SINP;
  const rz = ry0 * SINP + rz0 * COSP;
  const depth = -rz, s = FOC / depth;
  return { X: CXV + x * s, Y: CYV - ry * s, s, depth };
}

type Seg = { t: 's'; from: number; to: number; dur: number } | { t: 'd'; c: number; amp: number; dur: number; cyc: number };
type Timeline = { segs: Seg[]; total: number };

function mulberry32(a: number): () => number {
  return function () { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const smooth = (k: number): number => k * k * (3 - 2 * k);

// Full sweeps, but a sweep sometimes PAUSES partway on a row and re-scans a 1-2 row band before
// continuing — the dwell is inserted MID-sweep (never at the top/bottom extreme) so the position is
// continuous into and out of it. The timeline always ends at the top, so the final head can loop it
// seamlessly (no visible jump/restart) while it waits for `done`.
function buildTimeline(i: number, target: number): Timeline {
  const rng = mulberry32(((i + 1) * 2654435761) >>> 0);
  const segs: Seg[] = [];
  let total = 0, pos = 0, dir = 1;
  while (total < target) {
    const to = dir > 0 ? 1 : 0;
    if (rng() < 0.55 && target - total > 2200) {
      const mid = pos + (to - pos) * (0.3 + rng() * 0.4); // pause 30-70% into the sweep (always mid-page)
      const d1 = Math.max(140, HALF * Math.abs(mid - pos));
      segs.push({ t: 's', from: pos, to: mid, dur: d1 }); total += d1;
      const dd = 900 + rng() * 700; // slow re-scan
      segs.push({ t: 'd', c: mid, amp: 0.035 + rng() * 0.03, dur: dd, cyc: 2 + Math.floor(rng() * 2) }); total += dd;
      const d2 = Math.max(140, HALF * Math.abs(to - mid));
      segs.push({ t: 's', from: mid, to, dur: d2 }); total += d2;
    } else {
      segs.push({ t: 's', from: pos, to, dur: HALF }); total += HALF;
    }
    pos = to; dir = -dir;
  }
  if (pos !== 0) { segs.push({ t: 's', from: pos, to: 0, dur: HALF }); total += HALF; } // end at top → seamless loop
  return { segs, total };
}
const TL: Timeline[] = DUR.map((d, i) => buildTimeline(i, d));

// ---- module state ----
const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
let ctx: CanvasRenderingContext2D | null = null;
let doneEl: HTMLElement | null = null;
let stopBtn: HTMLButtonElement | null = null;
let rowEls: HTMLElement[] = [];
let cbs: { onScored: () => void; onComplete: () => void; onStop: () => void } = { onScored: () => {}, onComplete: () => {}, onStop: () => {} };

let Theta = Math.PI / 2, ThetaTarget = Math.PI / 2, activeIdx = 0, beamOn = false, scanStart = 0;
let doneReached = false, sequenceRunning = false, runToken = 0, stopped = false, completing = false;
let rafId: number | undefined;

// The user hit Stop below the conveyor: freeze the animation and let main abort scoring + return Home.
function onStopClick(): void {
  if (stopped || completing) return;
  stopped = true; runToken++; beamOn = false;      // cancel the running sequence, freeze the frame
  if (stopBtn) stopBtn.style.display = 'none';
  cbs.onStop();
}

const pnow = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, reduce ? Math.min(ms, 60) : ms));

function scanPos(): number {
  if (!beamOn) return 0;
  const tl = TL[activeIdx]!;
  let e = (pnow() - scanStart) % tl.total;
  for (const s of tl.segs) {
    if (e <= s.dur) {
      if (s.t === 's') return s.from + (s.to - s.from) * smooth(e / s.dur);
      return Math.max(0, Math.min(1, s.c + s.amp * Math.sin((e / s.dur) * s.cyc * 2 * Math.PI)));
    }
    e -= s.dur;
  }
  return 0;
}

function rr(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath(); c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r); c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
}

function scannerPos(i: number): { x: number; z: number } {
  const a = i * STEP + Theta;
  return { x: R * Math.cos(a), z: R * Math.sin(a) };
}

function drawTrack(c: CanvasRenderingContext2D): void {
  c.save();
  c.strokeStyle = 'rgba(0,0,0,.14)'; c.lineWidth = 1.3; c.beginPath();
  for (let k = 0; k <= 72; k++) { const a = (k / 72) * 2 * Math.PI, p = proj(R * Math.cos(a), 0, R * Math.sin(a)); if (k) c.lineTo(p.X, p.Y); else c.moveTo(p.X, p.Y); }
  c.closePath(); c.stroke();
  c.strokeStyle = 'rgba(0,0,0,.10)'; c.lineWidth = 1;
  for (let i = 0; i < N; i++) { const a = i * STEP + Theta, pi = proj((R - 7) * Math.cos(a), 0, (R - 7) * Math.sin(a)), po = proj((R + 7) * Math.cos(a), 0, (R + 7) * Math.sin(a)); c.beginPath(); c.moveTo(pi.X, pi.Y); c.lineTo(po.X, po.Y); c.stroke(); }
  c.restore();
}

// a compact industrial laser-scanner head, aimed at the document
function drawScanner(c: CanvasRenderingContext2D, i: number): void {
  const pp = scannerPos(i), g = proj(pp.x, 0, pp.z), s = g.s, act = i === activeIdx;
  const docMid = proj(0, DH * 0.5, 0), ang = Math.atan2(docMid.Y - g.Y, docMid.X - g.X);
  c.save(); c.fillStyle = 'rgba(0,0,0,' + (0.06 * s).toFixed(3) + ')';
  c.beginPath(); c.ellipse(g.X, g.Y + 2 * s, 16 * s, 5 * s, 0, 0, 2 * Math.PI); c.fill(); c.restore();

  c.save(); c.translate(g.X, g.Y); c.rotate(ang); c.scale(s, s); c.lineJoin = 'round';
  const ink = act ? '#d99a00' : 'rgba(40,40,40,.5)';
  c.strokeStyle = 'rgba(0,0,0,.28)'; c.lineWidth = 1.4; c.beginPath(); c.moveTo(-17, 0); c.lineTo(-22, 0); c.stroke();
  if (act) { c.shadowColor = 'rgba(255,190,23,.7)'; c.shadowBlur = 9; }
  c.fillStyle = act ? '#fff6e0' : '#eeede7'; c.strokeStyle = ink; c.lineWidth = 1.5;
  rr(c, -17, -9.5, 24, 19, 3.5); c.fill(); c.stroke(); c.shadowBlur = 0;
  c.fillStyle = act ? 'rgba(255,190,23,.22)' : 'rgba(0,0,0,.07)'; c.strokeStyle = ink; c.lineWidth = 1.3;
  rr(c, 6, -7, 9, 14, 2); c.fill(); c.stroke();
  c.fillStyle = act ? '#e6a900' : 'rgba(0,0,0,.35)';
  if (act) c.globalAlpha = 0.6 + 0.4 * Math.sin(pnow() / 170);
  rr(c, 12, -5.5, 2.4, 11, 1); c.fill(); c.globalAlpha = 1;
  c.fillStyle = act ? '#d99a00' : 'rgba(0,0,0,.3)'; c.beginPath(); c.arc(-11, -4.5, 1.6, 0, 2 * Math.PI); c.fill();
  c.restore();
}

function drawDoc(c: CanvasRenderingContext2D): void {
  const BL = proj(-DW / 2, 0, 0), BR = proj(DW / 2, 0, 0), TR = proj(DW / 2, DH, 0), TL2 = proj(-DW / 2, DH, 0);
  c.save();
  c.beginPath(); c.moveTo(BL.X, BL.Y); c.lineTo(BR.X, BR.Y); c.lineTo(TR.X, TR.Y); c.lineTo(TL2.X, TL2.Y); c.closePath();
  c.fillStyle = '#ffffff'; c.fill(); c.lineWidth = 1.6; c.strokeStyle = 'rgba(0,0,0,.30)'; c.stroke();
  const f1 = proj(DW / 2 - 22, DH, 0), f2 = proj(DW / 2, DH - 26, 0);
  c.beginPath(); c.moveTo(f1.X, f1.Y); c.lineTo(TR.X, TR.Y); c.lineTo(f2.X, f2.Y); c.closePath();
  c.fillStyle = '#ececea'; c.fill(); c.strokeStyle = 'rgba(0,0,0,.22)'; c.lineWidth = 1.2; c.stroke();
  c.strokeStyle = 'rgba(0,0,0,.22)'; c.lineWidth = 2;
  for (let k = 0; k < 9; k++) { const yy = DH * (0.88 - k * 0.092), wf = k % 3 === 2 ? 0.6 : 0.82, a = proj((-DW / 2) * 0.82, yy, 0), b = proj((DW / 2) * wf, yy, 0); c.beginPath(); c.moveTo(a.X, a.Y); c.lineTo(b.X, b.Y); c.stroke(); }
  c.restore();

  if (beamOn) {
    const yc = DH * (1 - scanPos()), w1 = proj(-DW / 2, yc, 0), w2 = proj(DW / 2, yc, 0);
    const gh = proj(-DW / 2, Math.min(DH, yc + 7), 0), gl = proj(-DW / 2, Math.max(0, yc - 7), 0);
    c.save();
    c.strokeStyle = 'rgba(255,190,23,.18)'; c.lineWidth = Math.abs(gh.Y - gl.Y) + 6; c.beginPath(); c.moveTo(w1.X, w1.Y); c.lineTo(w2.X, w2.Y); c.stroke();
    c.shadowColor = 'rgba(255,190,23,.9)'; c.shadowBlur = 8; c.strokeStyle = '#ffbe17'; c.lineWidth = 2.4; c.lineCap = 'round';
    c.beginPath(); c.moveTo(w1.X, w1.Y); c.lineTo(w2.X, w2.Y); c.stroke();
    c.restore();
  }
}

// the laser FAN: a sheet of light from the aperture that lands as the line on the page
function drawBeam(c: CanvasRenderingContext2D): void {
  if (!beamOn) return;
  const pp = scannerPos(activeIdx), apex = proj(pp.x, 30, pp.z);
  const yc = DH * (1 - scanPos()), bL = proj(-DW / 2, yc, 0), bR = proj(DW / 2, yc, 0);
  c.save();
  const grad = c.createLinearGradient(apex.X, apex.Y, (bL.X + bR.X) / 2, (bL.Y + bR.Y) / 2);
  grad.addColorStop(0, 'rgba(255,207,69,.55)'); grad.addColorStop(1, 'rgba(255,190,23,.10)');
  c.fillStyle = grad;
  c.beginPath(); c.moveTo(apex.X, apex.Y); c.lineTo(bL.X, bL.Y); c.lineTo(bR.X, bR.Y); c.closePath(); c.fill();
  c.shadowColor = 'rgba(255,190,23,.8)'; c.shadowBlur = 7; c.strokeStyle = 'rgba(255,207,69,.9)'; c.lineWidth = 2; c.lineCap = 'round';
  c.beginPath(); c.moveTo(apex.X, apex.Y); c.lineTo((bL.X + bR.X) / 2, (bL.Y + bR.Y) / 2); c.stroke();
  c.restore();
}

function drawFrame(): void {
  const c = ctx; if (!c) return;
  c.clearRect(0, 0, SW, SH);
  drawTrack(c);
  const items: { t: 's' | 'd'; i: number; depth: number }[] = [];
  for (let i = 0; i < N; i++) { const pp = scannerPos(i); items.push({ t: 's', i, depth: proj(pp.x, 0, pp.z).depth }); }
  items.push({ t: 'd', i: -1, depth: proj(0, 0, 0).depth });
  items.sort((a, b) => b.depth - a.depth);
  for (const it of items) { if (it.t === 's') drawScanner(c, it.i); else { drawDoc(c); drawBeam(c); } }
}

function frame(): void {
  Theta += (ThetaTarget - Theta) * 0.16;
  drawFrame();
  rafId = requestAnimationFrame(frame);
}
function startRender(): void { if (rafId === undefined) rafId = requestAnimationFrame(frame); }
function stopRender(): void { if (rafId !== undefined) { cancelAnimationFrame(rafId); rafId = undefined; } }

function setRow(i: number, cls: string): void { const r = rowEls[i]; if (r) r.className = 'sl-row' + (cls ? ' ' + cls : ''); }

async function runSequence(token: number): Promise<void> {
  completing = false;
  Theta = Math.PI / 2; ThetaTarget = Math.PI / 2; activeIdx = 0; beamOn = false;
  rowEls.forEach((r) => (r.className = 'sl-row'));
  doneEl?.classList.remove('show');
  if (stopBtn) stopBtn.style.display = ''; // Stop is available while the scan runs
  startRender();
  for (let i = 0; i < N; i++) {
    if (token !== runToken) return;
    activeIdx = i; ThetaTarget = Math.PI / 2 - i * STEP; setRow(i, 'scanning');
    await wait(ROTATE_MS); if (token !== runToken) return;
    await wait(DOCK_MS); if (token !== runToken) return;
    beamOn = true; scanStart = pnow();
    if (i < N - 1) {
      await wait(TL[i]!.total);
    } else {
      const start = pnow();
      await wait(TL[i]!.total);
      while (!doneReached && pnow() - start < LAST_CAP) { if (token !== runToken) return; await wait(HALF); }
    }
    if (token !== runToken) return;
    beamOn = false; setRow(i, 'done');
    await wait(GAP_MS);
  }
  if (token !== runToken) return;
  completing = true;
  if (stopBtn) stopBtn.style.display = 'none'; // past the point of stopping
  doneEl?.classList.add('show');
  cbs.onScored();
  await wait(HOLD_MS); if (token !== runToken) return;
  cbs.onComplete(); // main fades the whole view (header + scanner) and hands off to Quick Snapshot
}

function startSequence(): void {
  if (sequenceRunning || !ctx) return;
  sequenceRunning = true;
  const token = runToken;
  void runSequence(token).finally(() => { if (token === runToken) sequenceRunning = false; });
}

/** Build the canvas + panel once into `host`. */
export function mountScanLab(host: HTMLElement, callbacks: { onScored: () => void; onComplete: () => void; onStop: () => void }): void {
  cbs = callbacks;
  host.innerHTML = '';
  // Stop row — directly below the page header, centred in the workspace, so it is always reachable
  // without scrolling (it must never sit under the tall conveyor).
  const stopRow = document.createElement('div'); stopRow.className = 'sl-stoprow';
  const stop = document.createElement('button'); stop.type = 'button'; stop.className = 'sl-stop';
  stop.textContent = 'Stop'; stop.style.display = 'none'; stop.addEventListener('click', onStopClick);
  stopRow.appendChild(stop); stopBtn = stop;
  host.appendChild(stopRow);

  const stage = document.createElement('div'); stage.className = 'sl-stage';
  const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
  const canvas = document.createElement('canvas'); canvas.className = 'sl-canvas';
  canvas.width = SW * dpr; canvas.height = SH * dpr; canvas.style.maxWidth = '860px';
  canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', 'A rotating conveyor of laser scanners reading the transcript across eleven skills');
  const cx = canvas.getContext('2d'); if (cx) { cx.scale(dpr, dpr); ctx = cx; }
  stage.appendChild(canvas);

  const panel = document.createElement('div'); panel.className = 'sl-panel';
  const h = document.createElement('h4'); h.className = 'sl-h'; h.textContent = 'Now Analysing:'; panel.appendChild(h);
  const rowsWrap = document.createElement('div'); rowsWrap.className = 'sl-rows';
  rowEls = SKILLS.map((name) => {
    const row = document.createElement('div'); row.className = 'sl-row';
    const ind = document.createElement('span'); ind.className = 'sl-ind';
    const nm = document.createElement('span'); nm.className = 'sl-nm'; nm.textContent = name;
    row.appendChild(ind); row.appendChild(nm); rowsWrap.appendChild(row); return row;
  });
  panel.appendChild(rowsWrap);
  const done = document.createElement('div'); done.className = 'sl-done'; done.textContent = 'Scoring complete. Opening your score analysis.';
  doneEl = done; panel.appendChild(done);
  stage.appendChild(panel);
  host.appendChild(stage);
  drawFrame(); // an initial static frame so there is no blank flash before the run starts
}

/** Drive the scanner off the scoring phase (call from setPhase). */
export function scanLabPhase(phase: string): void {
  if (!ctx) return;
  if (phase === 'importing') { stopped = false; resetScanLab(); startSequence(); return; } // a fresh import clears Stop
  if (stopped) return; // the user hit Stop — ignore late phases (idle from the abort, a late done) until re-import
  if (phase === 'idle') { resetScanLab(); return; }
  if (phase === 'done') { doneReached = true; startSequence(); return; }
  startSequence(); // imported / running
}

/** Stop + reset the scanner (a new import, Clear, or a failed import). Does NOT clear the Stop latch. */
export function resetScanLab(): void {
  runToken++; sequenceRunning = false; completing = false; doneReached = false; beamOn = false;
  activeIdx = 0; Theta = Math.PI / 2; ThetaTarget = Math.PI / 2;
  rowEls.forEach((r) => (r.className = 'sl-row'));
  doneEl?.classList.remove('show');
  if (stopBtn) stopBtn.style.display = 'none';
  stopRender(); drawFrame();
}
